import { Link } from 'react-router-dom';
import { Phone, Zap, Home as HomeIcon, Building, Factory, ShieldCheck, Clock, Award } from 'lucide-react';
import { ContactForm } from '@/components/ContactForm';
import { PaymentSection } from '@/components/PaymentSection';

const HomePage = () => {
  const services = [
    {
      icon: HomeIcon,
      title: 'Residential',
      description: 'Complete electrical solutions for your home. From installations to repairs, we keep your family safe.',
    },
    {
      icon: Building,
      title: 'Commercial',
      description: 'Professional electrical services for businesses. Minimize downtime and maximize efficiency.',
    },
    {
      icon: Factory,
      title: 'Industrial',
      description: 'Heavy-duty electrical work for industrial facilities. Reliable power for your operations.',
    },
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: 'Licensed & Certified',
      description: 'Fully licensed with degree-level education in electrical systems.',
    },
    {
      icon: Clock,
      title: 'Prompt Service',
      description: 'Fast response times. We understand electrical issues can\'t wait.',
    },
    {
      icon: Award,
      title: 'Quality Work',
      description: 'Every job done right the first time. Your satisfaction guaranteed.',
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://plus.unsplash.com/premium_photo-1661908782924-de673a5c6988?fm=jpg&q=60&w=3000&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 gradient-overlay" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="animate-fadeIn">
              <span className="inline-block bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
                Professional Electrical Services
              </span>
              <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase text-white leading-tight mb-6">
                Build. Repair.
                <span className="text-amber-500"> Maintain.</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 max-w-xl">
                Expert electrical services for residential, commercial, and industrial needs. 
                Quality workmanship you can trust.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn stagger-2">
              <a
                href="tel:+19047137611"
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/25"
              >
                <Phone className="w-5 h-5" />
                Call (904) 713-7611
              </a>
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105"
              >
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
              What We Do
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-900 mt-2">
              Our Electrical Services
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              From simple repairs to complex installations, we handle all your electrical needs 
              with precision and care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-card-${service.title.toLowerCase()}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-lg card-hover animate-fadeIn stagger-${index + 1}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-colors">
                    <service.icon className="w-6 h-6 text-amber-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-slate-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="features-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
                Why Choose Us
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-900 mt-2 mb-6">
                Trusted Electrical Experts
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                Solid Repair LLC brings professional expertise and dedication to every project. 
                With formal education in electrical systems and years of hands-on experience, 
                we deliver results you can count on.
              </p>
              
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div
                    key={feature.title}
                    data-testid={`feature-item-${index}`}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-slate-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <a
                  href="tel:+19047137611"
                  data-testid="features-call-btn"
                  className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300 hover:scale-105"
                >
                  <Phone className="w-5 h-5" />
                  Get a Free Estimate
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://plus.unsplash.com/premium_photo-1661929137248-2544fd28de13?fm=jpg&q=60&w=800&auto=format&fit=crop"
                alt="Professional Electrician"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <Zap className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="text-3xl font-bold">10+</p>
                    <p className="text-slate-400 text-sm">Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Section */}
      <PaymentSection />

      {/* Contact Section */}
      <section data-testid="contact-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
                Get In Touch
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-900 mt-2 mb-6">
                Request a Quote
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                Need electrical work done? Fill out the form and we'll get back to you promptly. 
                Or give us a call - we're always happy to discuss your project.
              </p>

              <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                <h3 className="font-bold text-slate-900 mb-4">Prefer to call?</h3>
                <a
                  href="tel:+19047137611"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-3 text-2xl font-bold text-amber-600 hover:text-amber-500 transition-colors"
                >
                  <Phone className="w-6 h-6" />
                  (904) 713-7611
                </a>
              </div>

              <p className="text-slate-500 text-sm">
                Available Monday - Saturday, 7am - 7pm
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
